import type { ReactNode } from 'react';
import clsx from 'clsx';

import { Button } from '@lib/ui';

export interface HeaderProps {
  /** Logo element */
  logo?: ReactNode;
  /** Called when menu button is clicked */
  onMenuClick?: () => void;
  /** Whether to show the menu toggle button */
  showMenuToggle?: boolean;
  /** Search input configuration */
  search?: {
    placeholder?: string;
    onSearch?: (query: string) => void;
    shortcutKey?: string;
  };
  /** Right side actions/buttons */
  actions?: ReactNode;
  /** Additional className */
  className?: string;
  /** Whether to use bordered skin (vs shadow) */
  bordered?: boolean;
}

const SearchIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth={1.5}
    stroke="currentColor"
    className="size-4"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z"
    />
  </svg>
);

const MenuIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth={1.5}
    stroke="currentColor"
    className="size-6"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5"
    />
  </svg>
);

const SlashIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="22" height="20" aria-hidden="true">
    <path
      fill="none"
      stroke="currentColor"
      d="M3.5.5h12c1.7 0 3 1.3 3 3v13c0 1.7-1.3 3-3 3h-12c-1.7 0-3-1.3-3-3v-13c0-1.7 1.3-3 3-3z"
      opacity="0.4"
    />
    <path fill="currentColor" d="M11.8 6L8 15.1h-.9L10.8 6h1z" />
  </svg>
);

export function Header({
  logo,
  onMenuClick,
  showMenuToggle = true,
  search,
  actions,
  className,
  bordered = false,
}: HeaderProps) {
  return (
    <header
      className={clsx(
        'sticky top-0 z-20 flex h-16 shrink-0 items-center justify-between px-4 backdrop-blur-sm backdrop-saturate-150',
        bordered
          ? 'border-b border-gray-200 bg-white/80 dark:border-dark-600 dark:bg-dark-900/80'
          : 'bg-white/80 shadow-sm dark:bg-dark-750/80',
        className
      )}
    >
      <div className="flex items-center gap-3">
        {showMenuToggle && (
          <Button
            variant="flat"
            isIcon
            className="size-9 rounded-full lg:hidden"
            onClick={onMenuClick}
          >
            <MenuIcon />
          </Button>
        )}
        {logo}
      </div>

      <div className="flex items-center gap-2">
        {search && (
          <>
            {/* Desktop search */}
            <button
              onClick={() => search.onSearch?.('')}
              className="hidden h-8 w-64 items-center justify-between gap-2 rounded-full border border-gray-200 px-3 text-xs hover:border-gray-400 dark:border-dark-500 dark:hover:border-dark-400 sm:flex"
            >
              <div className="flex items-center gap-2">
                <SearchIcon />
                <span className="text-gray-400 dark:text-dark-300">
                  {search.placeholder || 'Search...'}
                </span>
              </div>
              {search.shortcutKey && <SlashIcon />}
            </button>

            {/* Mobile search */}
            <Button
              variant="flat"
              isIcon
              className="size-9 rounded-full sm:hidden"
              onClick={() => search.onSearch?.('')}
            >
              <SearchIcon />
            </Button>
          </>
        )}
        {actions}
      </div>
    </header>
  );
}

Header.displayName = 'Header';
